/**
 * @file src/pages/device-settings/components/DeviceOverviewTab.jsx
 * @description First tab of Device Settings — grid of device cards with
 * key info (serial number, VAT, environment, industry) and both
 * compliance/production status pills.
 *
 * Two empty states:
 *  1. No company yet  → "create a company first" message
 *  2. No devices yet  → "no devices found / try adjusting search" message
 *
 * Pure presentational. The parent (DeviceSettingsContent) handles loading
 * states, search filtering, and onSelectDevice callbacks.
 */

import React from "react";
import { motion } from "framer-motion";
import Icon from "components/AppIcon";
import StatusBadge from "./StatusBadge";

// ---------------------------------------------------------------------------
// Empty states
// ---------------------------------------------------------------------------

const EmptyState = ({ title, message }) => (
  <div className="text-center py-12">
    <Icon
      name="Monitor"
      size={48}
      className="text-muted-foreground mx-auto mb-4"
    />
    <h3 className="text-lg font-medium text-foreground mb-2">{title}</h3>
    <p className="text-muted-foreground">{message}</p>
  </div>
);

// ---------------------------------------------------------------------------
// DeviceCard
// ---------------------------------------------------------------------------

const DeviceCard = ({ device }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="bg-muted/30 border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover-lift"
  >
    {/* Header */}
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Monitor" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="font-medium text-foreground">
            {device.deviceSerialNumber}
          </h3>
          <p className="text-sm text-muted-foreground">
            {device.organizationName}
          </p>
        </div>
      </div>
      <div className="flex space-x-2">
        <StatusBadge status={device.certificates.compliance.status} />
        <StatusBadge status={device.certificates.production.status} />
      </div>
    </div>

    {/* Body */}
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4 text-sm">
        <Field label="VAT Number" value={device.vatNumber} />
        <Field label="Invoice Type" value={device.invoiceType} />
        <Field label="Environment" value={device.environment} />
        <Field label="Industry" value={device.industry} />
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="text-xs text-muted-foreground">
          Created: {new Date(device.createdAt).toLocaleDateString()}
          {device.lastUsed && (
            <span className="ml-2">
              • Last used: {new Date(device.lastUsed).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </div>
  </motion.div>
);

const Field = ({ label, value }) => (
  <div>
    <span className="text-muted-foreground">{label}:</span>
    <p className="font-medium text-foreground">{value}</p>
  </div>
);

// ---------------------------------------------------------------------------
// DeviceOverviewTab
// ---------------------------------------------------------------------------

const DeviceOverviewTab = ({ devices, company }) => {
  if (!company?.legalName && devices?.length === 0) {
    return (
      <EmptyState
        title="No Company Found"
        message="First create a Company than start configuring devices."
      />
    );
  }

  if (devices?.length === 0) {
    return (
      <EmptyState
        title="No devices found"
        message="No devices match your search criteria. Try adjusting your search terms."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-foreground">
          Registered Devices
        </h2>
        <span className="text-sm text-muted-foreground">
          {devices?.length} device{devices?.length !== 1 ? "s" : ""} found
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {devices?.map((device) => (
          <DeviceCard key={device.deviceId} device={device} />
        ))}
      </div>
    </div>
  );
};

export default DeviceOverviewTab;
